import { compact } from 'lodash-es'

interface GetMailtoHrefParams {
  to?: string
  subject?: string
  body?: string
}

/** A line break survives percent-encoding into the decoded header value, where it forges a second header — so collapse rather than preserve. RFC 6068 §5's CRLF rule covers the body alone. */
const flattenLineBreaks = (value: string): string => value.replace(/[\r\n]+/g, ' ')

const encodeSubject = (value: string): string => encodeURIComponent(flattenLineBreaks(value))

/** RFC 6068 §5 requires body line breaks to be CRLF, so normalize before encoding — patching `%0A` afterwards would turn an existing CRLF into `%0D%0D%0A`. */
const encodeBody = (value: string): string => encodeURIComponent(value.replace(/\r\n|\r|\n/g, '\r\n'))

/** `encodeURIComponent` would mangle an address into `a%2Bb%40corp.co`, so escape only the delimiters that could open a new header field. */
const encodeRecipient = (value: string): string => flattenLineBreaks(value).replace(/[\s?&#%<>"]/g, encodeURIComponent)

/**
 * Builds a fully-encoded `mailto:` href.
 *
 * Empty or omitted fields are dropped, so a call with no header fields yields no trailing `?`.
 * Values are encoded, never decoded — a body carrying an already-encoded URL keeps its escapes.
 * Line breaks survive only in `body`; in `to` and `subject` they collapse to spaces, since a
 * decoded break there would let the value forge a second header.
 */
export const getMailtoHref = ({ to, subject, body }: GetMailtoHrefParams): string => {
  const fields = compact([subject && `subject=${encodeSubject(subject)}`, body && `body=${encodeBody(body)}`])

  return compact([`mailto:${to ? encodeRecipient(to) : ''}`, fields.join('&')]).join('?')
}
